import React, { useEffect, useState } from "react";
import { db, auth, storage } from "@/lib/firebase";
import { collection, getDocs, query, where, updateDoc, doc, onSnapshot, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { FileText, Upload, Users, Search, CreditCard, Award } from "lucide-react";
import MessageDialog from "@/components/ui/MessageDialog";

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  registrationType?: "online" | "walk-in";
}

interface Enrollment {
  id: string;
  studentId: string;
  courseId: string;
  status: string;
  certificateUrl?: string;
  branch: string;
  registrationType: "online" | "walk-in";
  licenseCodes?: string[]; // Changed to array for multiple license codes
  licenseCodeIds?: string[]; // Changed to array for multiple license code IDs
  licenseCodeAssignedAt?: any;
  licenseCodeAssignedBy?: string;
}

interface Course {
  id: string;
  title: string;
}

interface LicenseCode {
  id: string;
  code: string;
  description: string;
  price: number;
  isActive: boolean;
}

const StaffUploadCertificates: React.FC = () => {
  const [userBranch, setUserBranch] = useState<string>("");
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [licenseCodes, setLicenseCodes] = useState<LicenseCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"all" | "completed" | "pending">("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog states
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showLicenseCodeDialog, setShowLicenseCodeDialog] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment | null>(null);
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [assigningLicenseCode, setAssigningLicenseCode] = useState(false);
  const [selectedLicenseCodeIds, setSelectedLicenseCodeIds] = useState<string[]>([]);
  const [licenseCodeNotes, setLicenseCodeNotes] = useState("");
  const [message, setMessage] = useState<{ open: boolean; text: string; type?: "success" | "error" | "info" }>({ 
    open: false, text: "" 
  });

  useEffect(() => {
    const setupData = async () => {
      if (!auth.currentUser) return;
      
      try {
        // Get staff user's branch
        const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
        const userData = userDoc.data();
        const branch = userData?.branch || "";
        setUserBranch(branch);

        // Set up real-time listeners
        const unsubscribers: (() => void)[] = [];

        // Listen to all enrollments for this branch (not just completed)
        const enrollmentsQuery = query(
          collection(db, "enrollments"), 
          where("branch", "==", branch)
        );
        const enrollmentsUnsubscribe = onSnapshot(enrollmentsQuery, (snapshot) => {
          setEnrollments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Enrollment[]);
        });
        unsubscribers.push(enrollmentsUnsubscribe);

        // Listen to students
        const studentsUnsubscribe = onSnapshot(collection(db, "users"), (snapshot) => {
          const allUsers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as any[];
          setStudents(allUsers.filter(user => user.role === "student") as Student[]);
        });
        unsubscribers.push(studentsUnsubscribe);

        // Listen to courses
        const coursesUnsubscribe = onSnapshot(collection(db, "courses"), (snapshot) => {
          setCourses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Course[]);
        });
        unsubscribers.push(coursesUnsubscribe);

        // Listen to license codes
        const licenseCodesUnsubscribe = onSnapshot(collection(db, "licenseCodes"), (snapshot) => {
          setLicenseCodes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as LicenseCode[]);
        });
        unsubscribers.push(licenseCodesUnsubscribe);

        setLoading(false);

        // Cleanup function
        return () => {
          unsubscribers.forEach(unsubscribe => unsubscribe());
        };
      } catch (error) {
        console.error("Error setting up certificate upload data:", error);
        setMessage({ open: true, text: "Failed to load data.", type: "error" });
        setLoading(false);
      }
    };

    setupData();
  }, []);

  const handleCertificateUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedEnrollment || !certificateFile) {
      setMessage({ open: true, text: "Please select a file to upload.", type: "error" });
      return;
    }

    setUploading(true);
    try {
      // Upload certificate to Storage
      const timestamp = Date.now();
      const storageRef = ref(storage, `certificates/${selectedEnrollment.id}_${timestamp}_${certificateFile.name}`);
      await uploadBytes(storageRef, certificateFile);
      const url = await getDownloadURL(storageRef);
      
      // Update enrollment with certificate URL
      await updateDoc(doc(db, "enrollments", selectedEnrollment.id), { 
        certificateUrl: url,
        certificateUploadedAt: new Date(),
        certificateUploadedBy: auth.currentUser!.uid
      });
      
      setMessage({ open: true, text: "Certificate uploaded successfully!", type: "success" });
      setShowUploadDialog(false);
      setSelectedEnrollment(null);
      setCertificateFile(null);
    } catch (error: any) {
      console.error("Error uploading certificate:", error);
      setMessage({ open: true, text: "Failed to upload certificate: " + error.message, type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleAssignLicenseCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedEnrollment || selectedLicenseCodeIds.length === 0) {
      setMessage({ open: true, text: "Please select at least one license code.", type: "error" });
      return;
    }

    setAssigningLicenseCode(true);
    try {
      const selectedLicenseCodes = licenseCodes.filter(lc => selectedLicenseCodeIds.includes(lc.id));
      const licenseCodeStrings = selectedLicenseCodes.map(lc => lc.code);
      
      // Get existing license codes and merge with new ones
      const existingLicenseCodeIds = selectedEnrollment.licenseCodeIds || [];
      const existingLicenseCodes = selectedEnrollment.licenseCodes || [];
      
      const updatedLicenseCodeIds = [...new Set([...existingLicenseCodeIds, ...selectedLicenseCodeIds])];
      const updatedLicenseCodes = [...new Set([...existingLicenseCodes, ...licenseCodeStrings])];
      
      // Update enrollment with license codes
      await updateDoc(doc(db, "enrollments", selectedEnrollment.id), {
        licenseCodes: updatedLicenseCodes,
        licenseCodeIds: updatedLicenseCodeIds,
        licenseCodeAssignedAt: new Date(),
        licenseCodeAssignedBy: auth.currentUser!.uid,
        licenseCodeNotes: licenseCodeNotes,
      });
      
      setMessage({ open: true, text: `${selectedLicenseCodeIds.length} license code(s) assigned successfully!`, type: "success" });
      setShowLicenseCodeDialog(false);
      setSelectedEnrollment(null);
      setSelectedLicenseCodeIds([]);
      setLicenseCodeNotes("");
    } catch (error: any) {
      console.error("Error assigning license code:", error);
      setMessage({ open: true, text: "Failed to assign license code: " + error.message, type: "error" });
    } finally {
      setAssigningLicenseCode(false);
    }
  };

  const getStudentInfo = (studentId: string) => {
    return students.find(s => s.id === studentId);
  };

  const getCourseInfo = (courseId: string) => {
    return courses.find(c => c.id === courseId);
  };

  const getRegistrationTypeBadge = (registrationType: "online" | "walk-in") => {
    if (registrationType === "walk-in") {
      return { variant: "secondary" as const, text: "Walk-in", className: "!bg-orange-100 !text-orange-700" };
    }
    return { variant: "secondary" as const, text: "Online", className: "!bg-blue-100 !text-blue-700" };
  };

  const getStatusBadge = (enrollment: Enrollment) => {
    switch (enrollment.status) {
      case "completed":
        return { variant: "secondary" as const, text: "Completed", className: "!bg-blue-100 !text-blue-700" };
      case "approved":
        return { variant: "secondary" as const, text: "Approved", className: "!bg-green-100 !text-green-700" };
      case "pending":
        return { variant: "secondary" as const, text: "Pending", className: "!bg-yellow-100 !text-yellow-700" };
      case "partial_payment":
        return { variant: "secondary" as const, text: "Partial Payment", className: "!bg-orange-100 !text-orange-700" };
      default:
        return { variant: "outline" as const, text: enrollment.status, className: "" };
    }
  };

  // Filter enrollments based on tab and search
  const getFilteredEnrollments = () => {
    let filtered = enrollments;

    // Filter by tab
    if (activeTab === "completed") {
      filtered = enrollments.filter(e => e.status === "completed");
    } else if (activeTab === "pending") {
      filtered = enrollments.filter(e => e.status !== "completed");
    }

    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(enrollment => {
        const student = getStudentInfo(enrollment.studentId);
        const course = getCourseInfo(enrollment.courseId);
        const searchLower = searchTerm.toLowerCase();
        
        return (
          student?.firstName?.toLowerCase().includes(searchLower) ||
          student?.lastName?.toLowerCase().includes(searchLower) ||
          student?.phone?.includes(searchTerm) ||
          student?.email?.toLowerCase().includes(searchLower) ||
          course?.title?.toLowerCase().includes(searchLower) ||
          enrollment.licenseCodes?.some(code => code.toLowerCase().includes(searchLower))
        );
      });
    }

    return filtered;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading certificate management...</div>
      </div>
    );
  }

  const filteredEnrollments = getFilteredEnrollments();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Certificate & License Management</h2>
          <p className="text-muted-foreground">Manage certificates and license codes for enrolled students</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">
            <Users className="h-3 w-3 mr-1" />
            {filteredEnrollments.length} Students
          </Badge>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
        <Button
          variant={activeTab === "all" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveTab("all")}
        >
          All Students
        </Button>
        <Button
          variant={activeTab === "pending" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveTab("pending")}
        >
          Pending/In Progress
        </Button>
        <Button
          variant={activeTab === "completed" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveTab("completed")}
        >
          Completed
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search students..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Students List */}
      <div className="grid gap-4">
        {filteredEnrollments.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {activeTab === "completed" ? "No completed students found" : 
                 activeTab === "pending" ? "No pending students found" : 
                 "No students found"}
              </h3>
              <p className="text-muted-foreground">
                {searchTerm ? "Try adjusting your search criteria." : 
                 activeTab === "completed" ? "Students who have completed their courses will appear here." :
                 "Enrolled students will appear here for certificate and license management."}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredEnrollments.map((enrollment) => {
            const student = getStudentInfo(enrollment.studentId);
            const course = getCourseInfo(enrollment.courseId);
            const regTypeBadge = getRegistrationTypeBadge(enrollment.registrationType);
            const statusBadge = getStatusBadge(enrollment);
            
            return (
              <Card key={enrollment.id}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="text-lg font-medium">
                          {student?.firstName} {student?.lastName}
                        </h3>
                        <Badge variant={regTypeBadge.variant} className={regTypeBadge.className}>
                          {regTypeBadge.text}
                        </Badge>
                        <Badge variant={statusBadge.variant} className={statusBadge.className}>
                          {statusBadge.text}
                        </Badge>
                        {enrollment.certificateUrl && (
                          <Badge variant="secondary" className="!bg-green-100 !text-green-700">
                            ✓ Certificate Uploaded
                          </Badge>
                        )}
                        {enrollment.licenseCodes && enrollment.licenseCodes.length > 0 && (
                          <Badge variant="secondary" className="!bg-purple-100 !text-purple-700">
                            📋 Licenses: {enrollment.licenseCodes.join(", ")}
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <div>📚 Course: {course?.title || 'Unknown Course'}</div>
                        <div>📞 {student?.phone}</div>
                        {student?.email && <div>✉️ {student?.email}</div>}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2 ml-6">
                      {/* License Code Assignment - Always show button for adding more codes */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedEnrollment(enrollment);
                          setShowLicenseCodeDialog(true);
                        }}
                      >
                        <CreditCard className="h-4 w-4 mr-1" />
                        {(!enrollment.licenseCodes || enrollment.licenseCodes.length === 0) 
                          ? "Assign License Code" 
                          : "Add More Codes"}
                      </Button>
                      
                      {/* Certificate Management */}
                      {enrollment.certificateUrl ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(enrollment.certificateUrl, '_blank')}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          View Certificate
                        </Button>
                      ) : (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => {
                            setSelectedEnrollment(enrollment);
                            setShowUploadDialog(true);
                          }}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Upload Certificate
                        </Button>
                      )}
                      
                      {/* Replace certificate option */}
                      {enrollment.certificateUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedEnrollment(enrollment);
                            setShowUploadDialog(true);
                          }}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          Replace Certificate
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Upload Certificate Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Upload Certificate
            </DialogTitle>
          </DialogHeader>
          {selectedEnrollment && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded">
                <div className="text-sm space-y-1">
                  <div><strong>Student:</strong> {(() => {
                    const student = getStudentInfo(selectedEnrollment.studentId);
                    return `${student?.firstName} ${student?.lastName}`;
                  })()}</div>
                  <div><strong>Course:</strong> {getCourseInfo(selectedEnrollment.courseId)?.title || 'Unknown'}</div>
                  <div><strong>Status:</strong> {selectedEnrollment.status}</div>
                </div>
              </div>
              
              <form onSubmit={handleCertificateUpload} className="space-y-4">
                <div>
                  <label htmlFor="certificate" className="block text-sm font-medium mb-2">
                    Certificate File *
                  </label>
                  <input
                    id="certificate"
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={(e) => setCertificateFile(e.target.files?.[0] || null)}
                    className="w-full border rounded p-2"
                    required
                  />
                  <div className="text-xs text-muted-foreground mt-1">
                    Accepted formats: PDF, JPG, PNG (Max 10MB)
                  </div>
                </div>
                
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowUploadDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={uploading}>
                    {uploading ? "Uploading..." : "Upload Certificate"}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign License Code Dialog */}
      <Dialog open={showLicenseCodeDialog} onOpenChange={setShowLicenseCodeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Assign License Code
            </DialogTitle>
          </DialogHeader>
          {selectedEnrollment && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded">
                <div className="text-sm space-y-1">
                  <div><strong>Student:</strong> {(() => {
                    const student = getStudentInfo(selectedEnrollment.studentId);
                    return `${student?.firstName} ${student?.lastName}`;
                  })()}</div>
                  <div><strong>Course:</strong> {getCourseInfo(selectedEnrollment.courseId)?.title || 'Unknown'}</div>
                  <div><strong>Status:</strong> {selectedEnrollment.status}</div>
                  {selectedEnrollment.licenseCodes && selectedEnrollment.licenseCodes.length > 0 && (
                    <div><strong>Current Licenses:</strong> 
                      <div className="mt-1 flex flex-wrap gap-1">
                        {selectedEnrollment.licenseCodes.map((code, index) => (
                          <span key={index} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                            {code}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <form onSubmit={handleAssignLicenseCode} className="space-y-4">
                <div>
                  <Label>License Codes * (Select one or more)</Label>
                  <div className="border rounded p-3 max-h-48 overflow-y-auto space-y-2">
                    {licenseCodes
                      .filter(lc => lc.isActive !== false) // Show codes that don't have isActive:false
                      .map(licenseCode => {
                        const isSelected = selectedLicenseCodeIds.includes(licenseCode.id);
                        const isAlreadyAssigned = selectedEnrollment?.licenseCodeIds?.includes(licenseCode.id);
                        
                        return (
                          <div key={licenseCode.id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`license-${licenseCode.id}`}
                              checked={isSelected}
                              disabled={isAlreadyAssigned}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedLicenseCodeIds([...selectedLicenseCodeIds, licenseCode.id]);
                                } else {
                                  setSelectedLicenseCodeIds(selectedLicenseCodeIds.filter(id => id !== licenseCode.id));
                                }
                              }}
                              className="rounded"
                            />
                            <label 
                              htmlFor={`license-${licenseCode.id}`} 
                              className={`flex-1 text-sm ${isAlreadyAssigned ? 'text-gray-400' : 'cursor-pointer'}`}
                            >
                              <span className="font-medium">{licenseCode.code}</span> - {licenseCode.description} 
                              <span className="text-green-600 ml-1">(₱{licenseCode.price})</span>
                              {isAlreadyAssigned && <span className="text-gray-500 ml-2">(Already assigned)</span>}
                            </label>
                          </div>
                        );
                      })}
                    {licenseCodes.filter(lc => lc.isActive !== false).length === 0 && (
                      <div className="text-gray-500 text-sm">No license codes available</div>
                    )}
                  </div>
                  {selectedLicenseCodeIds.length > 0 && (
                    <div className="text-sm text-blue-600 mt-1">
                      {selectedLicenseCodeIds.length} license code(s) selected
                    </div>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="licenseNotes">Notes (Optional)</Label>
                  <Input
                    id="licenseNotes"
                    value={licenseCodeNotes}
                    onChange={(e) => setLicenseCodeNotes(e.target.value)}
                    placeholder="Any additional notes..."
                  />
                </div>
                
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowLicenseCodeDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={assigningLicenseCode}>
                    {assigningLicenseCode ? "Assigning..." : "Assign License Code"}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <MessageDialog 
        open={message.open} 
        onClose={() => setMessage({ ...message, open: false })} 
        message={message.text} 
        type={message.type} 
      />
    </div>
  );
};

export default StaffUploadCertificates;
